import {
  DateRangePicker,
  DateRange,
} from '@mui/x-date-pickers-pro/DateRangePicker';
import { Box, SxProps, TextField, Theme } from '@mui/material';
import React from 'react';
import { Control, Controller, FieldErrors, FieldValues } from 'react-hook-form';

export const DateRangeField = function ({
  id,
  name,
  label,
  control,
  startLabel,
  endLabel,
  disablePast,
  errors,
  minDate,
  maxDate,
  inputSx,
  separator = 'to',
}: {
  id: string;
  name: string;
  label: string;
  control: Control<FieldValues>;
  format?: string;
  startLabel?: string;
  endLabel?: string;
  disablePast?: boolean;
  errors?: FieldErrors | null;
  minDate?: Date;
  maxDate?: Date;
  /** Optional style overrides applied to both date TextFields. */
  inputSx?: SxProps<Theme>;
  /** Separator shown between the two inputs. Defaults to "to". */
  separator?: React.ReactNode;
}) {
  const [open, setOpen] = React.useState(false);
  const [isAccept, setIsAccept] = React.useState(false);
  const [isTextField, setIsTextField] = React.useState(false);

  const createTitle = (text: string) => {
    const result = text.replace(/([A-Z])/g, ' $1');
    return result.charAt(0).toUpperCase() + result.slice(1);
  };

  return (
    <Controller
      rules={{ required: true }}
      control={control}
      name={name}
      render={({ field: { onChange, value } }) => {
        return (
          <DateRangePicker
            open={open}
            minDate={minDate ?? undefined}
            maxDate={maxDate ?? undefined}
            onAccept={() => {
              setIsAccept(true);
            }}
            onClose={() => {
              setIsAccept(false);
              if (!isTextField) {
                setOpen(false);
                setIsTextField(false);
              }
            }}
            onOpen={() => {
              if (!isAccept) {
                setOpen(true);
              }
            }}
            disablePast={disablePast}
            calendars={1}
            label={label}
            value={value}
            onChange={(date: DateRange<unknown>) => onChange(date)}
            renderInput={(startProps, endProps) => {
              return (
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignSelf: 'center',
                    width: '100%',
                  }}
                >
                  <TextField
                    {...startProps}
                    sx={[
                      { flex: 2 },
                      ...(Array.isArray(inputSx) ? inputSx : [inputSx]),
                    ]}
                    variant="standard"
                    label={(startLabel && createTitle(startLabel)) || 'start'}
                    error={!!errors && !!errors[id]?.message}
                    helperText={!!errors && errors[id]?.message}
                    onFocus={() => {
                      setIsTextField(true);
                    }}
                    onBlur={() => {
                      setIsTextField(false);
                    }}
                    onClick={() => {
                      setOpen(true);
                    }}
                  />
                  <Box
                    sx={{
                      // Use margin, not padding: some containers (e.g. the
                      // resource details drawer) apply a blanket
                      // `.MuiBox-root { padding: 0 }` reset that would zero px
                      // here, collapsing the separator against the next input.
                      mx: 2,
                      display: 'flex',
                      alignItems: 'flex-end',
                      justifyContent: 'center',
                    }}
                  >
                    {separator}
                  </Box>
                  <TextField
                    {...endProps}
                    sx={[
                      { flex: 2 },
                      ...(Array.isArray(inputSx) ? inputSx : [inputSx]),
                    ]}
                    label={(endLabel && createTitle(endLabel)) || 'end'}
                    error={!!errors && !!errors[id]?.message}
                    variant="standard"
                    onFocus={() => {
                      setIsTextField(true);
                    }}
                    onBlur={() => {
                      setIsTextField(false);
                    }}
                    onClick={() => {
                      setOpen(true);
                    }}
                  />
                </div>
              );
            }}
          />
        );
      }}
    />
  );
};
